import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { formatPrice } from "../utils/formatPrice";
import { trackEvent } from "../utils/analytics";
import boatPlaceholder from "../assets/images/boat.jpg";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { YachtDetails } from "../types/YachtDetails";
import HeaderWithSearch from "../components/common/HeaderWithSearch";
import MapboxMap from "../components/MapboxMap";
import DoneIcon from "@mui/icons-material/Done";
import CircleIcon from "@mui/icons-material/Circle";
import {
  searchAndFiltersYachts,
  fetchYachtModels,
} from "../utils/boatApi";
import { getTodayDate, getOneWeekLaterDate } from "../utils/getTodayDate";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import Tooltip from "@mui/material/Tooltip";
import { Carousel } from "antd";
import FiltersWidget from "./common/FiltersWidget";
import AllFiltersBox from "./common/AllFiltersBox";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  setAmenities,
  setCounts,
  mergeCounts,
  setFilterNextAndPrev,
  setSearchResultsSlice,
  setYachtLocation,
} from "../store/searchSlice";
import MobileSearchBar from "./common/MobileSearchBar";
import HeaderBar from "./HeaderBar";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Container,
  Chip,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getEquipmentIcon } from "../utils/equipmentIcons";
import { formatCount } from "../utils/formatCount";
import CarouselArrows from "./common/CarouselArrows";
import { getBoatStatus } from "../utils/boatTypes";
import { addToComparator, removeFromComparator } from "../store/comparatorSlice";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

interface CompareToggleProps {
  yachtId: string;
  model: string;
  imageUrl: string;
  price: number;
  dateFrom: string;
  dateTo: string;
  status: string;
}

const CompareToggleButton: React.FC<CompareToggleProps> = React.memo(
  ({ yachtId, model, imageUrl, price, dateFrom, dateTo, status }) => {
    const dispatch = useDispatch();
    const inComparator = useSelector((state: RootState) => state.comparator.items.some(i => i.yachtId === yachtId));
    const isFull = useSelector((state: RootState) => state.comparator.items.length >= 4);
    const full = !inComparator && isFull;
    return (
      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Tooltip
          title={full ? "Comparator full — remove a boat first" : inComparator ? "Remove from comparison" : "Compare up to 4 boats side by side"}
          arrow
          placement="top"
        >
          <span>
            <Button
              size="small"
              variant={inComparator ? "contained" : "outlined"}
              disabled={full}
              startIcon={<CompareArrowsIcon />}
              onClick={(e) => {
                e.stopPropagation();
                if (inComparator) {
                  dispatch(removeFromComparator(yachtId));
                } else {
                  dispatch(addToComparator({ yachtId, model, imageUrl, price, dateFrom, dateTo, status }));
                  trackEvent('compare_added', { yacht_model: model });
                }
              }}
              sx={{
                fontSize: "12px",
                borderRadius: "20px",
                ...(inComparator ? {
                  bgcolor: "#1565c0",
                  "&:hover": { bgcolor: "#0d47a1" },
                } : {
                  borderColor: "#1565c0",
                  color: "#1565c0",
                  "&:hover": { bgcolor: "#e3f0ff", borderColor: "#1565c0" },
                }),
              }}
            >
              {inComparator ? "✓ Added" : "Compare"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }
);

interface YachtLocation {
  id: string;
  latitude: number;
  longitude: number;
}

const SearchResults: React.FC = () => {
  const [isHovered, setIsHovered] = useState(null);
  const [currentSlides, setCurrentSlides] = useState<any>([]);
  const carouselRefs = useRef<any>([]);
  const location = useLocation();
  const dispatch = useDispatch();

  const navigate = useNavigate(); //Use useNavigate for routing
  const searchData = useSelector((state: RootState) => state.search);
  // State to hold search results, loading state, and pagination
  const [searchResults, setSearchResults] = useState<
    { dateFrom: string; dateTo: string }[]
  >(searchData?.searchResults?.results);
  const [globalPriceRange, setGlobalPriceRange] = useState<{min: number, max: number} | null>(null);
  const [globalPriceHistogram, setGlobalPriceHistogram] = useState<number[]>([]);
  const [globalYearRange, setGlobalYearRange] = useState<{min: number, max: number} | null>(null);
  const [globalLengthRange, setGlobalLengthRange] = useState<{min: number, max: number} | null>(null);

  const prices = searchResults?.map((item: any) =>
    Number(item?.actualPrice),
  );
  const validPrices = prices?.filter((p: number) => p > 0) || [];
  const priceSliderMin = globalPriceRange?.min ?? (validPrices.length > 0 ? Math.floor(Math.min(...validPrices)) : 552);
  const priceSliderMax = globalPriceRange?.max ?? (validPrices.length > 0 ? Math.ceil(Math.max(...validPrices)) : 61658);
  // const years = searchResults?.map((item: any) =>
  //   new Date(item?.dateFrom).getFullYear()
  // );
  const years = searchResults?.map((item: any) => item?.yacht?.year);
  const M_TO_FT = 3.28084;
  const lengths = searchResults
    ?.map((item: any) => Math.round(Number(item?.yacht?.length) * M_TO_FT))
    .filter((l: number) => l > 0);
  const yearSliderMin = globalYearRange?.min ?? 1990;
  const yearSliderMax = globalYearRange?.max ?? new Date().getFullYear();
  const lengthSliderMinFt = globalLengthRange ? Math.floor(globalLengthRange.min * M_TO_FT) : 20;
  const lengthSliderMaxFt = globalLengthRange ? Math.ceil(globalLengthRange.max * M_TO_FT) : 120;

  // Convert pre-computed bin counts from API into representative midpoint values
  // so RangeSlider's generateHistogram function bins them correctly.
  const priceHistogramValues = useMemo(() => {
    if (globalPriceHistogram.length > 0 && globalPriceRange && globalPriceRange.max > globalPriceRange.min) {
      const { min, max } = globalPriceRange;
      const binSize = (max - min) / globalPriceHistogram.length;
      const values: number[] = [];
      globalPriceHistogram.forEach((count, i) => {
        const midpoint = min + (i + 0.5) * binSize;
        for (let j = 0; j < Math.round(count); j++) values.push(midpoint);
      });
      return values.length > 0 ? values : prices;
    }
    return prices;
  }, [globalPriceHistogram, globalPriceRange, prices]);

  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1); // Current page
  const [totalPages, setTotalPages] = useState<number>(1); // Total number of pages

  const [boatsFilter, setBoatsFilter] = useState<string[]>([]);
  const [cabinsFilter, setCabinsFilter] = useState<string[]>([]);
  const [bathroomFilters, setBathroomFilter] = useState<string[]>([]);
  const currentYear = new Date().getFullYear();

  // Helper function to generate default dates using existing utilities
  const getDefaultDates = () => {
    const todayISO = getTodayDate() + "T00:00:00";
    const oneWeekLaterISO = getOneWeekLaterDate() + "T00:00:00";

    return {
      defaultCheckIn: todayISO,
      defaultCheckOut: oneWeekLaterISO,
    };
  };

  const [priceFilter, setPriceFilter] = useState<number[]>([552, 61658]);
  const [yearFilter, setyearFilter] = useState<number[]>([1990, currentYear]);
  const [lengthFilter, setLengthFilter] = useState<number[]>([20, 120]);
  const [amenitiesFilter, setAmenitiesFilter] = useState<string[]>([]);
  const [orderFilter, setOrderFilter] = useState<string>("");
  const [buildersInput, setBuildersInput] = useState<string[]>([]);
  const [yachtModels, setYachtModels] = useState<string[]>([]);
  const [initialLoad, setInitialLoad] = React.useState<boolean>(true);

  // Track whether the user explicitly set each range filter (vs auto-synced from fleet bounds).
  // Refs drive API/effect logic (no re-render); state drives UI (blue active badge on button).
  const priceMinUserSet   = useRef(false);
  const priceMaxUserSet   = useRef(false);
  const yearMinUserSet    = useRef(false);
  const yearMaxUserSet    = useRef(false);
  const lengthMinUserSet  = useRef(false);
  const lengthMaxUserSet  = useRef(false);
  const [priceUserSet, setPriceUserSet]   = useState(false);
  const [yearUserSet,  setYearUserSet]    = useState(false);
  const [lengthUserSet, setLengthUserSet] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModal2, setIsOpenModal2] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [clusterYachts, setClusterYachts] = useState<YachtLocation[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const previousLocationRef = useRef<string>("");

  const BACKEND_URL = process.env.REACT_APP_API_URL || '';

  const imageUrl = (originalUrl: string | undefined, full = false): string => {
    if (!originalUrl) {
      return boatPlaceholder;
    }
    if (!originalUrl.includes('booking-manager.com')) {
      return originalUrl;
    }
    if (full) {
      return originalUrl;
    }
    return `${BACKEND_URL}/api/image/?url=${encodeURIComponent(originalUrl)}&w=400&h=280&q=82`;
  };

  // Fetch yacht models once on component mount
  React.useEffect(() => {
    const loadYachtModels = async () => {
      try {
        const models = await fetchYachtModels();
        setYachtModels(models);
      } catch (error) {
        console.error("Failed to load yacht models:", error);
        // Fallback to hardcoded options if API fails
        setYachtModels(["Bavaria", "Jeanneau", "Beneteau", "Sunseeker"]);
      }
    };

    loadYachtModels();
  }, []);

  // Wrapper setters passed to children — mark the filter as user-set, or clear the flag
  // when the value returns to the current fleet bounds (user clicked × / Clear).
  const handleUserPriceChange = useCallback((v: number[]) => {
    const minSet = globalPriceRange ? v[0] !== globalPriceRange.min : false;
    const maxSet = globalPriceRange ? v[1] !== globalPriceRange.max : false;
    priceMinUserSet.current = minSet;
    priceMaxUserSet.current = maxSet;
    setPriceUserSet(minSet || maxSet);
    setPriceFilter(v);
  }, [globalPriceRange]);

  const handleUserYearChange = useCallback((v: number[]) => {
    const minSet = globalYearRange ? v[0] !== globalYearRange.min : false;
    const maxSet = globalYearRange ? v[1] !== globalYearRange.max : false;
    yearMinUserSet.current = minSet;
    yearMaxUserSet.current = maxSet;
    setYearUserSet(minSet || maxSet);
    setyearFilter(v);
  }, [globalYearRange]);

  const handleUserLengthChange = useCallback((v: number[]) => {
    const boundsMin = globalLengthRange ? Math.floor(globalLengthRange.min * M_TO_FT) : 20;
    const boundsMax = globalLengthRange ? Math.ceil(globalLengthRange.max  * M_TO_FT) : 120;
    const minSet = v[0] !== boundsMin;
    const maxSet = v[1] !== boundsMax;
    lengthMinUserSet.current = minSet;
    lengthMaxUserSet.current = maxSet;
    setLengthUserSet(minSet || maxSet);
    setLengthFilter(v);
  }, [globalLengthRange]);

  // Sync filter state to fleet bounds whenever the bounds change — but only when the
  // user hasn't explicitly set that filter (ref is false).
  useEffect(() => {
    if (globalPriceRange && !priceMinUserSet.current && !priceMaxUserSet.current)
      setPriceFilter([globalPriceRange.min, globalPriceRange.max]);
  }, [globalPriceRange]);

  useEffect(() => {
    if (globalYearRange && !yearMinUserSet.current && !yearMaxUserSet.current)
      setyearFilter([globalYearRange.min, globalYearRange.max]);
  }, [globalYearRange]);

  useEffect(() => {
    if (globalLengthRange && !lengthMinUserSet.current && !lengthMaxUserSet.current)
      setLengthFilter([Math.floor(globalLengthRange.min * M_TO_FT), Math.ceil(globalLengthRange.max * M_TO_FT)]);
  }, [globalLengthRange]);

  const formatToISOString = (dateStr: any) => {
    return new Date(dateStr.replace(" ", "T")).toISOString().slice(0, -5);
  };

  const params = new URLSearchParams(location.search);

  const fetchSeachFilteredYachts = (check: boolean) => {
    let count = 0;
    if (boatsFilter.length > 0) count++;
    if (cabinsFilter.length > 0) count++;
    if (bathroomFilters.length > 0) count++;
    if (amenitiesFilter.length > 0) count++;
    if (orderFilter !== "") count++;
    if (buildersInput.length > 0) count++;
    if (clusterYachts.length > 0) count++;
    if (priceMinUserSet.current || priceMaxUserSet.current) count++;
    if (yearMinUserSet.current || yearMaxUserSet.current) count++;
    if (lengthMinUserSet.current || lengthMaxUserSet.current) count++;

    setFilterCount(count);
    if (initialLoad) {
      // setInitialLoad(false);
      return;
    }
    if (isOpenModal2 && check) {
      return;
    }

    // const params = new URLSearchParams(location.search);

    if (boatsFilter.length > 0)
      params.set("yacht_types", boatsFilter.join(","));
    else params.delete("yacht_types");
    if (clusterYachts.length > 0) {
      params.set("cluster_locations", JSON.stringify(clusterYachts));
    } else {
      params.delete("cluster_locations");
    }

    if (cabinsFilter.length > 0) params.set("cabins", cabinsFilter.join(","));
    else params.delete("cabins");
    if (bathroomFilters.length > 0)
      params.set("bathrooms", bathroomFilters.join(","));
    else params.delete("bathrooms");

    if (amenitiesFilter.length > 0)
      params.set("amenities", amenitiesFilter.join(","));
    else params.delete("amenities");

    if (buildersInput.length > 0) params.set("model_names", buildersInput.join(","));
    else params.delete("model_names");

    if (orderFilter) params.set("order_by", orderFilter);
    else params.delete("order_by");

    if (priceMinUserSet.current) params.set("min_price", String(priceFilter[0]));
    else params.delete("min_price");
    if (priceMaxUserSet.current) params.set("max_price", String(priceFilter[1]));
    else params.delete("max_price");

    if (yearMinUserSet.current) params.set("year_min", String(yearFilter[0]));
    else params.delete("year_min");
    if (yearMaxUserSet.current) params.set("year_max", String(yearFilter[1]));
    else params.delete("year_max");

    if (lengthMinUserSet.current) params.set("min_length", String(lengthFilter[0]));
    else params.delete("min_length");
    if (lengthMaxUserSet.current) params.set("max_length", String(lengthFilter[1]));
    else params.delete("max_length");

    navigate(`/search-results?${params.toString()}`, { replace: true });
  };

  React.useEffect(() => {
    fetchSeachFilteredYachts(true);

    // const fetchFilteredYachts = async () => {
    //   setLoading(true);
    //   const filters = {
    //     date_from: searchResults
    //       ? searchResults.length > 0
    //         ? formatToISOString(searchResults[0].dateFrom)
    //         : "2025-03-01T12:00:00"
    //       : "2025-03-01T12:00:00",
    //     date_to: searchResults
    //       ? searchResults.length > 0
    //         ? formatToISOString(searchResults[0].dateTo)
    //         : "2025-03-08T04:00:00"
    //       : "2025-03-08T04:00:00",
    //     country: searchData.selectedLocation?.shortName,
    //     currency: "EUR",
    //     // yacht_type: "sailboat",
    //     bed: 0,
    //     page: 1,
    //     city: searchData.selectedLocation?.city,
    //     id: "",
    //     ...(JSON.stringify(priceFilter) !== JSON.stringify(defaultPrice)
    //       ? {
    //           min_price: Math.min(...priceFilter),
    //           max_price: Math.max(...priceFilter),
    //         }
    //       : {}),
    //     ...(JSON.stringify(yearFilter) !== JSON.stringify(defaultYear)
    //       ? {
    //           year_min: Math.min(...yearFilter),
    //           year_max: Math.max(...yearFilter),
    //         }
    //       : {}),
    //     ...(cabinsFilter.length > 0
    //       ? { cabins: cabinsFilter.map((item) => parseInt(item)) }
    //       : {}),
    //     ...(boatsFilter.length > 0 ? { yacht_types: boatsFilter } : {}),
    //     ...(amenitiesFilter.length > 0 ? { amenities: amenitiesFilter } : {}),
    //     ...(buildersInput.length > 0 ? { model_names: buildersInput } : {}),
    //     ...(orderFilter !== "" ? { order_by: orderFilter } : {}),
    //     ...(clusterYachts.length > 0
    //       ? { cluster_locations: clusterYachts }
    //       : {}),
    //     // order_by: orderFilter,
    //   };
    //   setPage(1);

    //   const yachtResults = await filterYachts(filters);
    //   if (yachtResults) {
    //     setSearchResults(yachtResults?.results);
    //     dispatch(setSearchResultsSlice(yachtResults));
    //     setTotalPages(Math.ceil(yachtResults?.count / 20));
    //     dispatch(
    //       setFilterNextAndPrev({
    //         filterNext: yachtResults?.next || "",
    //         filterPrev: yachtResults?.previous || "",
    //       })
    //     );
    //   }
    //   setLoading(false);
    // };

    // fetchFilteredYachts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boatsFilter,
    cabinsFilter,
    priceFilter,
    yearFilter,
    lengthFilter,
    amenitiesFilter,
    orderFilter,
    buildersInput,
    clusterYachts,
    bathroomFilters,
  ]);
  const fetchFilteredYachts = async () => {
    await setIsOpenModal2(false);

    fetchSeachFilteredYachts(false);

    // setLoading(true);
    // const filters = {
    //   date_from: searchResults
    //     ? searchResults.length > 0
    //       ? formatToISOString(searchResults[0].dateFrom)
    //       : "2025-03-01T12:00:00"
    //     : "2025-03-01T12:00:00",
    //   date_to: searchResults
    //     ? searchResults.length > 0
    //       ? formatToISOString(searchResults[0].dateTo)
    //       : "2025-03-08T04:00:00"
    //     : "2025-03-08T04:00:00",
    //   country: searchData.selectedLocation?.shortName,
    //   currency: "EUR",
    //   // yacht_type: "sailboat",
    //   bed: 0,
    //   page: 1,
    //   city: searchData.selectedLocation?.city,
    //   id: "",
    //   ...(JSON.stringify(priceFilter) !== JSON.stringify(defaultPrice)
    //     ? {
    //         min_price: Math.min(...priceFilter),
    //         max_price: Math.max(...priceFilter),
    //       }
    //     : {}),
    //   ...(JSON.stringify(yearFilter) !== JSON.stringify(defaultYear)
    //     ? {
    //         year_min: Math.min(...yearFilter),
    //         year_max: Math.max(...yearFilter),
    //       }
    //     : {}),
    //   ...(cabinsFilter.length > 0
    //     ? { cabins: cabinsFilter.map((item) => parseInt(item)) }
    //     : {}),
    //   ...(boatsFilter.length > 0 ? { yacht_types: boatsFilter } : {}),
    //   ...(amenitiesFilter.length > 0 ? { amenities: amenitiesFilter } : {}),
    //   ...(buildersInput.length > 0 ? { model_names: buildersInput } : {}),
    //   ...(orderFilter !== "" ? { order_by: orderFilter } : {}),
    //   // order_by: orderFilter,
    // };
    // setPage(1);

    // const yachtResults = await filterYachts(filters);
    // if (yachtResults) {
    //   setSearchResults(yachtResults?.results);
    //   dispatch(setSearchResultsSlice(yachtResults));
    //   setTotalPages(Math.ceil(yachtResults?.count / 20));
    //   dispatch(
    //     setFilterNextAndPrev({
    //       filterNext: yachtResults?.next || "",
    //       filterPrev: yachtResults?.previous || "",
    //     })
    //   );
    // }
    // setLoading(false);
  };
  const clearFilters = async () => {
    setIsOpenModal2(false);
    priceMinUserSet.current  = false;
    priceMaxUserSet.current  = false;
    yearMinUserSet.current   = false;
    yearMaxUserSet.current   = false;
    lengthMinUserSet.current = false;
    lengthMaxUserSet.current = false;
    setBoatsFilter([]);
    setCabinsFilter([]);
    setBathroomFilter([]);
    setPriceUserSet(false);
    setYearUserSet(false);
    setLengthUserSet(false);
    setPriceFilter(globalPriceRange ? [globalPriceRange.min, globalPriceRange.max] : [552, 61658]);
    setyearFilter(globalYearRange ? [globalYearRange.min, globalYearRange.max] : [1990, currentYear]);
    setLengthFilter(globalLengthRange ? [Math.floor(globalLengthRange.min * M_TO_FT), Math.ceil(globalLengthRange.max * M_TO_FT)] : [20, 120]);
    setAmenitiesFilter([]);
    setOrderFilter("");
    setBuildersInput([]);
    setClusterYachts([]);
    setGlobalPriceRange(null);
    setGlobalPriceHistogram([]);
    setPage(1);

    // Directly clear all filter params from URL — don't rely on effect chain
    // (avoids stuck loading spinner when URL wouldn't otherwise change)
    const newParams = new URLSearchParams(location.search);
    ['yacht_types','cabins','bathrooms','amenities','model_names','order_by',
     'min_price','max_price','year_min','year_max','min_length','max_length',
     'cluster_locations'].forEach(k => newParams.delete(k));
    navigate(`/search-results?${newParams.toString()}`, { replace: true });
  };

  // State for hovered yacht coordinates
  const [hoveredLocation, setHoveredLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const { t } = useTranslation() as any;
  // Initial search parameters state, without setSearchParams

  // const [searchParams, setSearchParams] = useState({
  //   checkIn: searchData?.checkIn,
  //   checkOut: searchData?.checkOut,
  //   country: searchData.selectedLocation?.shortName,
  //   boatType: "",
  //   city: searchData.selectedLocation?.city,
  //   beds: 0,
  //   id: searchData.selectedLocation?.id
  //     ? searchData.selectedLocation?.id
  //     : "",
  // });

  const [searchParams, setSearchParams] = useState({
    date_from: "",
    date_to: "",
    country: "",
    // yacht_type: "",
    bed: 0,
    city: "",
    id: "",
    type: "",
  });

  const handleClusterClick = (clusterYachts: YachtLocation[]) => {
    setClusterYachts(clusterYachts);
  };

  const handleYachtHover = (latitude: number, longitude: number) => {
    setHoveredLocation({
      latitude: latitude ? latitude : 0,
      longitude: longitude ? longitude : 0,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const checkIn = params.get("date_from") || "";
    const checkOut = params.get("date_to") || "";
    const country = params.get("country") || "";
    const boatType = params.get("yacht_type") || "";
    const beds = parseInt(params.get("bed") || "0");
    const city = params.get("city") || "";
    const type = params.get("type") || "";
    
    // Get the correct ID parameter based on type
    let id = "";
    if (type === "base") {
      id = params.get("base_from_id") || "";
    } else if (type === "sailing_area") {
      id = params.get("sailing_area_id") || "";
    } else if (type === "island") {
      id = params.get("island_name") || "";
    } else {
      id = params.get("id") || "";
    }

    // Initialize previousLocationRef with current location on mount
    if (previousLocationRef.current === "") {
      previousLocationRef.current = `${country}-${city}-${id}-${type}`;
    }

    setSearchParams({
      date_from: checkIn,
      date_to: checkOut,
      country: country,
      // yacht_type: boatType,
      bed: beds,
      city: city,
      id: id,
      type: type,
    });

    if (checkIn && checkOut && !initialLoad) {
      handleSearch(
        checkIn,
        checkOut,
        country,
        boatType,
        beds,
        city,
        id,
        type,
        1,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, initialLoad]);

  useEffect(() => {
    setLoading(true);
    // Parse yacht types filter
    const yachtTypes = params.get("yacht_types");
    if (yachtTypes) {
      setBoatsFilter(yachtTypes.split(","));
    }

    // Parse cabins filter
    const cabins = params.get("cabins");
    if (cabins) {
      setCabinsFilter(cabins.split(","));
    }

    // Parse bathrooms filter
    const bathrooms = params.get("bathrooms");
    if (bathrooms) {
      setBathroomFilter(bathrooms.split(","));
    }

    // Parse amenities filter
    const amenities = params.get("amenities");
    if (amenities) {
      setAmenitiesFilter(amenities.split(","));
    }

    // Parse builders filter
    const builders = params.get("model_names");
    if (builders) {
      setBuildersInput(builders.split(","));
    }

    // Parse order filter
    const order = params.get("order_by");
    if (order) {
      setOrderFilter(order);
    }

    // Parse price filter — set per-handle flags independently
    const minPrice = params.get("min_price");
    const maxPrice = params.get("max_price");
    if (minPrice) { priceMinUserSet.current = true; }
    if (maxPrice) { priceMaxUserSet.current = true; }
    if (minPrice || maxPrice) setPriceUserSet(true);
    if (minPrice && maxPrice) setPriceFilter([Number(minPrice), Number(maxPrice)]);
    else if (minPrice) setPriceFilter(prev => [Number(minPrice), prev[1]]);
    else if (maxPrice) setPriceFilter(prev => [prev[0], Number(maxPrice)]);

    // Parse year filter — set per-handle flags independently
    const yearMin = params.get("year_min");
    const yearMax = params.get("year_max");
    if (yearMin) { yearMinUserSet.current = true; }
    if (yearMax) { yearMaxUserSet.current = true; }
    if (yearMin || yearMax) setYearUserSet(true);
    if (yearMin && yearMax) setyearFilter([Number(yearMin), Number(yearMax)]);
    else if (yearMin) setyearFilter(prev => [Number(yearMin), prev[1]]);
    else if (yearMax) setyearFilter(prev => [prev[0], Number(yearMax)]);

    // Parse length filter — set per-handle flags independently
    const minLength = params.get("min_length");
    const maxLength = params.get("max_length");
    if (minLength) { lengthMinUserSet.current = true; }
    if (maxLength) { lengthMaxUserSet.current = true; }
    if (minLength || maxLength) setLengthUserSet(true);
    if (minLength && maxLength) setLengthFilter([Number(minLength), Number(maxLength)]);
    else if (minLength) setLengthFilter(prev => [Number(minLength), prev[1]]);
    else if (maxLength) setLengthFilter(prev => [prev[0], Number(maxLength)]);

    setTimeout(() => {
      setInitialLoad(false);
    }, 1500);

    // setTotalPages(Math.ceil(searchData?.searchResults?.count / 20));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const handleSearch = async (
  //   checkIn: string,
  //   checkOut: string,
  //   country: string,
  //   boatType: string,
  //   beds: number,
  //   city: string,
  //   id: any,
  //   page: number
  // ) => {
  //   setLoading(true);

  //   try {
  //     // Call the searchYachts API
  //     const countResults = await searchCounts(
  //       checkIn || today,
  //       checkOut || oneWeekLater,
  //       country || "",
  //       "EUR", // Default currency
  //       boatType || "",
  //       beds,
  //       page, // Pass the current page
  //       city,
  //       id
  //     );
  //     const YachtsLocation = await searchYachtsLocation(
  //       checkIn,
  //       checkOut,
  //       country || "",
  //       "EUR", // Default currency
  //       boatType || "",
  //       beds,
  //       page, // Pass the current page
  //       city,
  //       id
  //     );
  //     const yachtResults = await searchYachts(
  //       checkIn,
  //       checkOut,
  //       country || "",
  //       "EUR", // Default currency
  //       boatType || "",
  //       beds,
  //       page, // Pass the current page
  //       city,
  //       id
  //     );

  //     // Check if yachtResults is null
  //     if (!yachtResults) {
  //       setSearchResults([]); // Set search results to an empty array
  //       setTotalPages(0); // Reset pagination
  //       return;
  //     }
  //     // If results exist, update the state
  //     dispatch(setYachtLocation(YachtsLocation?.response?.yachts || []));
  //     setSearchResults(yachtResults.response.results); // Update the search results
  //     setTotalPages(Math.ceil(yachtResults?.response.count / 20)); // Update total pages based on the result count
  //     dispatch(setSearchResultsSlice(yachtResults?.response));
  //     dispatch(setCounts(countResults));
  //     dispatch(setAmenities(yachtResults?.amenities || []));
  //   } catch (error) {
  //     // console.error("Search failed", error);
  //   } finally {
  //     setLoading(false); // Always stop loading spinner
  //   }
  // };

  // const handleSearch = async (
  //   checkIn: string,
  //   checkOut: string,
  //   country: string,
  //   boatType: string,
  //   beds: number,
  //   city: string,
  //   id: any,
  //   page: number
  // ) => {
  //   setLoading(true);
  //   try {
  //     const currency = "EUR";

  //     // Check if filters are applied
  //     const isFilterApplied =
  //       boatsFilter.length > 0 ||
  //       cabinsFilter.length > 0 ||
  //       amenitiesFilter.length > 0 ||
  //       orderFilter !== "" ||
  //       buildersFilter !== "" ||
  //       buildersInput !== "" ||
  //       priceFilter[0] !== 552 ||
  //       priceFilter[1] !== 61658 ||
  //       yearFilter[0] !== 1990 ||
  //       yearFilter[1] !== 2025 ||
  //       clusterYachts.length > 0;

  //     const filters = {
  //       date_from: formatToISOString(checkIn),
  //       date_to: formatToISOString(checkOut),
  //       country: country,
  //       currency: currency,
  //       bed: beds,
  //       page: page,
  //       city: city,
  //       id: id,
  //       ...(JSON.stringify(priceFilter) !== JSON.stringify(defaultPrice)
  //         ? {
  //             min_price: Math.min(...priceFilter),
  //             max_price: Math.max(...priceFilter),
  //           }
  //         : {}),
  //       ...(JSON.stringify(yearFilter) !== JSON.stringify(defaultYear)
  //         ? {
  //             year_min: Math.min(...yearFilter),
  //             year_max: Math.max(...yearFilter),
  //           }
  //         : {}),
  //       ...(cabinsFilter.length > 0
  //         ? { cabins: cabinsFilter.map((item) => parseInt(item)) }
  //         : {}),
  //       ...(boatsFilter.length > 0 ? { yacht_types: boatsFilter } : {}),
  //       ...(amenitiesFilter.length > 0 ? { amenities: amenitiesFilter } : {}),
  //       ...(buildersInput.length > 0 ? { model_names: buildersInput } : {}),
  //       ...(orderFilter !== "" ? { order_by: orderFilter } : {}),
  //       ...(clusterYachts.length > 0
  //         ? { cluster_locations: clusterYachts }
  //         : {}),
  //     };

  //     const [countResults, YachtsLocation, yachtResults] = await Promise.all([
  //       searchCounts(
  //         checkIn,
  //         checkOut,
  //         country,
  //         currency,
  //         boatType,
  //         beds,
  //         page,
  //         city,
  //         id
  //       ),
  //       searchYachtsLocation(
  //         checkIn,
  //         checkOut,
  //         country,
  //         currency,
  //         boatType,
  //         beds,
  //         page,
  //         city,
  //         id
  //       ),
  //       isFilterApplied
  //         ? filterYachts(filters)
  //         : searchYachts(
  //             checkIn,
  //             checkOut,
  //             country,
  //             currency,
  //             boatType,
  //             beds,
  //             page,
  //             city,
  //             id
  //           ),
  //     ]);

  //     if (!yachtResults || !yachtResults?.response) {
  //       setSearchResults([]);
  //       setTotalPages(0);
  //       return;
  //     }
  //     dispatch(setYachtLocation(YachtsLocation?.response?.yachts || []));
  //     setSearchResults(yachtResults.response.results || []);
  //     setTotalPages(Math.ceil(yachtResults?.response.count / 20));
  //     dispatch(setSearchResultsSlice(yachtResults?.response));
  //     dispatch(setCounts(countResults));
  //     dispatch(setAmenities(yachtResults?.amenities || []));
  //   } catch (error) {
  //     console.error("Search failed", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSearch = async (
    checkIn: string,
    checkOut: string,
    country: string,
    boatType: string,
    beds: number,
    city: string,
    id: any,
    type: string,
    page: number,
  ) => {
    setLoading(true);
    try {
      const currency = "EUR";
      const key =
        type === "base"
          ? "base_from_id"
          : type === "sailing_area"
            ? "sailing_area_id"
            : type === "island"
              ? "island_name"
              : "id";
      const filters = {
        date_from: formatToISOString(checkIn),
        date_to: formatToISOString(checkOut),
        country: country,
        currency: currency,
        bed: beds,
        page: page,
        city: city,
        yacht_type: boatType,
        [key]: id,
        ...(priceMinUserSet.current  ? { min_price:  priceFilter[0]  } : {}),
        ...(priceMaxUserSet.current  ? { max_price:  priceFilter[1]  } : {}),
        ...(yearMinUserSet.current   ? { year_min:   yearFilter[0]   } : {}),
        ...(yearMaxUserSet.current   ? { year_max:   yearFilter[1]   } : {}),
        ...(lengthMinUserSet.current ? { min_length: lengthFilter[0] } : {}),
        ...(lengthMaxUserSet.current ? { max_length: lengthFilter[1] } : {}),
        ...(cabinsFilter.length > 0
          ? { cabins: cabinsFilter.map((item) => parseInt(item)) }
          : {}),
        ...(bathroomFilters.length > 0
          ? { bathrooms: bathroomFilters.map((item) => parseInt(item)) }
          : {}),
        ...(boatsFilter.length > 0 ? { yacht_types: boatsFilter } : {}),
        ...(amenitiesFilter.length > 0 ? { amenities: amenitiesFilter } : {}),
        ...(buildersInput.length > 0 ? { model_names: buildersInput } : {}),
        ...(orderFilter !== "" ? { order_by: orderFilter } : {}),
        ...(clusterYachts.length > 0
          ? { cluster_locations: clusterYachts }
          : {}),
        type,
      };

      // let yachtResults = await searchAndFiltersYachts(filters);
      // Parallel API calls
      const [yachtResults] = await Promise.all([
        searchAndFiltersYachts(filters),
      ]);

      if (!yachtResults || !yachtResults?.response) {
        setSearchResults([]);
        dispatch(setYachtLocation([]));
        dispatch(setSearchResultsSlice([]));
        dispatch(setCounts({ yacht_types: [], cabins: [], total_yachts: 0 }));
        dispatch(setAmenities([]));

        setTotalPages(0);
        return;
      }
      dispatch(setYachtLocation(yachtResults?.response?.map?.yachts || []));
      setSearchResults(yachtResults?.response?.search.results || []);
      setTotalPages(Math.ceil(yachtResults?.response?.search?.count / 20));
      dispatch(setSearchResultsSlice(yachtResults?.response?.search));
      if (yachtResults?.response?.count) {
        dispatch(mergeCounts(yachtResults?.response?.count));
      }
      dispatch(setAmenities(yachtResults?.amenities || []));
      if (!priceMinUserSet.current && !priceMaxUserSet.current && yachtResults?.response?.price_range?.min > 0) {
        setGlobalPriceRange(yachtResults.response.price_range);
        if (yachtResults.response.price_histogram?.length) {
          setGlobalPriceHistogram(yachtResults.response.price_histogram);
        }
      }
      if (!yearMinUserSet.current && !yearMaxUserSet.current && yachtResults?.response?.year_range?.min > 0) {
        setGlobalYearRange(yachtResults.response.year_range);
      }
      if (!lengthMinUserSet.current && !lengthMaxUserSet.current && yachtResults?.response?.length_range?.min > 0) {
        setGlobalLengthRange(yachtResults.response.length_range);
      }
    } catch (error) {
      setSearchResults([]);
      dispatch(setYachtLocation([]));
      dispatch(setSearchResultsSlice([]));
      dispatch(setCounts({ yacht_types: [], cabins: [], total_yachts: 0 }));
      dispatch(setAmenities([]));
      console.error("Search failed", error);
    } finally {
      setLoading(false);
    }
  };

  // const handleSearchNext = async ({
  //   api,
  //   page,
  // }: {
  //   api: string;
  //   page: number;
  // }) => {
  //   setLoading(true);
  //   try {
  //     const isFilterApplied =
  //       boatsFilter.length > 0 ||
  //       cabinsFilter.length > 0 ||
  //       amenitiesFilter.length > 0 ||
  //       orderFilter !== "" ||
  //       buildersFilter !== "" ||
  //       buildersInput !== "" ||
  //       priceFilter[0] !== 552 ||
  //       priceFilter[1] !== 61658 ||
  //       yearFilter[0] !== 1990 ||
  //       yearFilter[1] !== 2025 ||
  //       clusterYachts.length > 0;

  //     const filters = {
  //       date_from: searchResults
  //         ? searchResults.length > 0
  //           ? formatToISOString(searchResults[0].dateFrom)
  //           : "2025-03-01T12:00:00"
  //         : "2025-03-01T12:00:00",
  //       date_to: searchResults
  //         ? searchResults.length > 0
  //           ? formatToISOString(searchResults[0].dateTo)
  //           : "2025-03-08T04:00:00"
  //         : "2025-03-08T04:00:00",
  //       country: searchData.selectedLocation?.shortName,
  //       currency: "EUR",
  //       // yacht_type: "sailboat",
  //       bed: 0,
  //       page: page,
  //       city: searchData.selectedLocation?.city,
  //       base_from_id: "",
  //       ...(JSON.stringify(priceFilter) !== JSON.stringify(defaultPrice)
  //         ? {
  //             min_price: Math.min(...priceFilter),
  //             max_price: Math.max(...priceFilter),
  //           }
  //         : {}),
  //       ...(JSON.stringify(yearFilter) !== JSON.stringify(defaultYear)
  //         ? {
  //             year_min: Math.min(...yearFilter),
  //             year_max: Math.max(...yearFilter),
  //           }
  //         : {}),
  //       ...(cabinsFilter.length > 0
  //         ? { cabins: cabinsFilter.map((item) => parseInt(item)) }
  //         : {}),
  //       ...(boatsFilter.length > 0 ? { yacht_types: boatsFilter } : {}),
  //       ...(amenitiesFilter.length > 0 ? { amenities: amenitiesFilter } : {}),
  //       ...(buildersInput.length > 0 ? { model_names: buildersInput } : {}),
  //       ...(orderFilter !== "" ? { order_by: orderFilter } : {}),
  //       ...(clusterYachts.length > 0
  //         ? { cluster_locations: clusterYachts }
  //         : {}),
  //       // order_by: orderFilter,
  //     };

  //     if (isFilterApplied) {
  //       const yachtResults = await filterYachts(filters);
  //       if (!yachtResults) {
  //         setSearchResults([]);
  //         setTotalPages(0);
  //         return;
  //       }

  //       setSearchResults(yachtResults.results);
  //       setTotalPages(Math.ceil(yachtResults.count / 20));
  //       const amenities: { label: string }[] = [];
  //       const seen = new Set<string>();

  //       if (yachtResults && Array.isArray(yachtResults.results)) {
  //         for (const item of yachtResults.results) {
  //           if (item.yacht?.equipment_json) {
  //             for (const equipment of item.yacht.equipment_json) {
  //               console.log("equipment", equipment);
  //               // if (equipment.name && !seen.has(equipment.name)) {
  //               //   seen.add(equipment.name);
  //               //   amenities.push({ label: equipment.name });
  //               // }
  //             }
  //           }
  //         }
  //       }

  //       dispatch(setSearchResultsSlice(yachtResults));
  //       dispatch(
  //         setFilterNextAndPrev({
  //           filterNext: yachtResults?.next || "",
  //           filterPrev: yachtResults?.previous || "",
  //         })
  //       );
  //       dispatch(setAmenities(amenities));
  //       setTotalPages(Math.ceil(yachtResults?.count / 20));
  //     } else {
  //       const yachtResults = await axios.get(api);
  //       setSearchResults(yachtResults.data.results);
  //       setTotalPages(Math.ceil(yachtResults.data.count / 20));
  //       const amenities: { label: string }[] = [];
  //       const seen = new Set<string>();

  //       if (yachtResults.data && Array.isArray(yachtResults.data.results)) {
  //         for (const item of yachtResults.data.results) {
  //           if (item.yacht?.equipment_json) {
  //             for (const equipment of item.yacht.equipment_json) {
  //               if (equipment.name && !seen.has(equipment.name)) {
  //                 seen.add(equipment.name);
  //                 amenities.push({ label: equipment.name });
  //               }
  //             }
  //           }
  //         }
  //       }

  //       dispatch(setSearchResultsSlice(yachtResults.data));
  //       dispatch(
  //         setFilterNextAndPrev({
  //           filterNext: yachtResults?.data.next || "",
  //           filterPrev: yachtResults?.data.previous || "",
  //         })
  //       );
  //       dispatch(setAmenities(amenities));
  //       setTotalPages(Math.ceil(yachtResults?.data.count / 20));
  //     }

  //     setPage(page);
  //   } catch (error) {
  //     console.error("Search failed", error);
  //   } finally {
  //     setLoading(false); // Always stop loading spinner
  //   }
  // };

  const handleSearchNext = async ({
    api: _api,
    page,
  }: {
    api: string;
    page: number;
  }) => {
    const key =
      searchParams.type === "base"
        ? "base_from_id"
        : searchParams.type === "sailing_area"
          ? "sailing_area_id"
          : searchParams.type === "island"
            ? "island_name"
            : "id";
    setLoading(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const { defaultCheckIn, defaultCheckOut } = getDefaultDates();
      const filters = {
        date_from: searchResults
          ? searchParams.date_from
            ? formatToISOString(searchParams.date_from)
            : defaultCheckIn
          : defaultCheckIn,
        date_to: searchResults
          ? searchParams.date_to
            ? formatToISOString(searchParams.date_to)
            : defaultCheckOut
          : defaultCheckOut,
        country: searchParams.country,
        currency: "EUR",
        // yacht_type: searchParams.yacht_type,
        bed: 0,
        page: page,
        city: searchParams.city,
        [key]: searchParams.id,
        ...(priceMinUserSet.current  ? { min_price:  priceFilter[0]  } : {}),
        ...(priceMaxUserSet.current  ? { max_price:  priceFilter[1]  } : {}),
        ...(yearMinUserSet.current   ? { year_min:   yearFilter[0]   } : {}),
        ...(yearMaxUserSet.current   ? { year_max:   yearFilter[1]   } : {}),
        ...(lengthMinUserSet.current ? { min_length: lengthFilter[0] } : {}),
        ...(lengthMaxUserSet.current ? { max_length: lengthFilter[1] } : {}),
        ...(cabinsFilter.length > 0
          ? { cabins: cabinsFilter.map((item) => parseInt(item)) }
          : {}),
        ...(bathroomFilters.length > 0
          ? { bathrooms: bathroomFilters.map((item) => parseInt(item)) }
          : {}),
        ...(boatsFilter.length > 0 ? { yacht_types: boatsFilter } : {}),
        ...(amenitiesFilter.length > 0 ? { amenities: amenitiesFilter } : {}),
        ...(buildersInput.length > 0 ? { model_names: buildersInput } : {}),
        ...(orderFilter !== "" ? { order_by: orderFilter } : {}),
        ...(clusterYachts.length > 0
          ? { cluster_locations: clusterYachts }
          : {}),
        type: searchParams?.type || "",
        // order_by: orderFilter,
      };

      const yachtResults = await searchAndFiltersYachts(filters);
      if (!yachtResults || !yachtResults?.response) {
        setSearchResults([]);
        setTotalPages(0);
        return;
      }

      setSearchResults(yachtResults?.response?.search?.results || []);
      setTotalPages(Math.ceil(yachtResults?.response?.search?.count / 20));

      dispatch(setSearchResultsSlice(yachtResults?.response?.search));
      dispatch(
        setFilterNextAndPrev({
          filterNext: yachtResults?.response?.search?.next || "",
          filterPrev: yachtResults?.response?.search?.previous || "",
        }),
      );
      dispatch(setAmenities(yachtResults?.amenities || []));
      setTotalPages(Math.ceil(yachtResults?.response?.search?.count / 20));
      if (!priceMinUserSet.current && !priceMaxUserSet.current && yachtResults?.response?.price_range?.min > 0) {
        setGlobalPriceRange(yachtResults.response.price_range);
        if (yachtResults.response.price_histogram?.length) {
          setGlobalPriceHistogram(yachtResults.response.price_histogram);
        }
      }
      if (!yearMinUserSet.current && !yearMaxUserSet.current && yachtResults?.response?.year_range?.min > 0) {
        setGlobalYearRange(yachtResults.response.year_range);
      }
      if (!lengthMinUserSet.current && !lengthMaxUserSet.current && yachtResults?.response?.length_range?.min > 0) {
        setGlobalLengthRange(yachtResults.response.length_range);
      }

      setPage(page);
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setLoading(false); // Always stop loading spinner
    }
  };

  const isFilterApplied =
    boatsFilter.length > 0 ||
    cabinsFilter.length > 0 ||
    amenitiesFilter.length > 0 ||
    bathroomFilters.length > 0 ||
    orderFilter !== "" ||
    buildersInput.length > 0 ||
    (priceMinUserSet.current || priceMaxUserSet.current) ||
    (yearMinUserSet.current || yearMaxUserSet.current) ||
    (lengthMinUserSet.current || lengthMaxUserSet.current);

  // Reset pagination and initiate a new search when triggered from HeaderWithSearch
  const handleNewSearch = (
    checkIn: string,
    checkOut: string,
    country: string,
    boatType: string,
    beds: any,
    city: string,
    id: any,
    type: string,
  ) => {
    setIsOpenModal(false);
    setPage(1); // Reset to the first page

    // Create a unique location identifier
    const currentLocation = `${country}-${city}-${id}-${type}`;
    
    // Clear map filter, price filter and global price range only if location has changed
    if (previousLocationRef.current !== currentLocation) {
      if (clusterYachts.length > 0) setClusterYachts([]);
      setGlobalPriceRange(null);
      setGlobalYearRange(null);
      setGlobalLengthRange(null);
      setGlobalPriceHistogram([]);
      // Range filter refs stay intact so API keeps filtering; state (badge) stays in sync.
      // Reset accumulated counts so the new location starts fresh.
      dispatch(setCounts({ yacht_types: [], cabins: [], total_yachts: 0, bathrooms: [] }));
      previousLocationRef.current = currentLocation;

      // If type/cabin/bathroom filters are active the main search will only return
      // filtered counts, leaving dropdowns with partial options. Fetch baseline
      // (unfiltered) counts for the new location in parallel so all options show.
      if (boatsFilter.length > 0 || cabinsFilter.length > 0 || bathroomFilters.length > 0) {
        const locKey = type === "base" ? "base_from_id"
          : type === "sailing_area" ? "sailing_area_id"
          : type === "island" ? "island_name"
          : "id";
        // Island type uses city as the island_name value (matching URL builder logic).
        const locValue = type === "island" ? city : id;
        searchAndFiltersYachts({
          date_from: formatToISOString(checkIn),
          date_to: formatToISOString(checkOut),
          country,
          currency: "EUR",
          bed: beds || 0,
          page: 1,
          city,
          [locKey]: locValue,
          type,
        }, { silent: true }).then(result => {
          if (result?.response?.count) {
            dispatch(mergeCounts(result.response.count));
          }
        });
      }
    }

    // const key =
    //   type === "base"
    //     ? "base_from_id"
    //     : type === "sailing_area"
    //       ? "sailing_area_id"
    //       : "id";

    const queryParams = new URLSearchParams({
      date_from: checkIn,
      date_to: checkOut,
      country,
      yacht_type: boatType,
      bed: beds?.toString() || "0",
      // [type]: id,
    });

    // Handle different location types properly
    if (type === "base") {
      queryParams.append("base_from_id", id);
    } else if (type === "sailing_area") {
      queryParams.append("sailing_area_id", id);
    } else if (type === "island") {
      queryParams.append("island_name", city);
    } else {
      // For city type, only add id parameter, not city as key
      if (id) queryParams.append("id", id);
    }

    if (city) queryParams.append("city", city);
    if (type) queryParams.append("type", type);

    navigate(`/search-results?${queryParams.toString()}`);
  };

  const handleYachtClick = (yacht: YachtDetails) => {
    trackEvent('yacht_viewed', {
      yacht_id: String(yacht.yachtId || ''),
      yacht_name: yacht.yacht?.name || '',
      yacht_model: yacht.yacht?.model || '',
      price: yacht.price || '',
    });
    const currentParams = new URLSearchParams(searchParams as any);
    if (yacht?.yachtId) {
      currentParams.set("yachtId", String(yacht.yachtId));
    }
    // Build the new query string
    const newQuery = currentParams.toString();

    // Redirect to yacht details page with updated query
    let url = `/yacht-details?${newQuery}`;
    window.open(url, "_blank");
    // navigate(`/yacht-details?${newQuery}`, {
    //   state: { from: "yachtsLists" },
    // });

    // localStorage.setItem("selectedYacht", JSON.stringify(yacht));

    // Navigate to the yacht details page
    // navigate(`/yacht-details`, { state: { from: "yachtsLists" } });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [searchBarHidden, setSearchBarHidden] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY && y > 80) setSearchBarHidden(true);
      else if (y < lastY) setSearchBarHidden(false);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openSearchBar = () => {
    setIsOpenModal(true);
  };
  const openSearchBar2 = () => {
    setIsOpenModal2(true);
  };
  const onClose = () => {
    setIsOpenModal(!isOpenModal);
  };
  const onClose2 = () => {
    setIsOpenModal2(!isOpenModal2);
  };

  // Add this helper function to calculate visible dots range
  const getVisibleDotsRange = (
    totalImages: any,
    currentIndex: any,
    maxDots = 5,
  ) => {
    if (totalImages <= maxDots) {
      return { start: 0, end: totalImages - 1 };
    }

    let start = Math.max(0, currentIndex - Math.floor(maxDots / 2));
    let end = Math.min(totalImages - 1, start + maxDots - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < maxDots) {
      start = Math.max(0, end - maxDots + 1);
    }

    return { start, end };
  };
  return (
    <Box>
      <HeaderBar />
      <Box position="sticky" top="calc(var(--banner-height, 0px) + var(--nav-height, 68px))" zIndex={999} sx={{ transition: 'transform 0.3s ease', transform: isMobile && searchBarHidden ? 'translateY(calc(-100% - var(--banner-height, 0px) - var(--nav-height, 68px)))' : 'none' }}>
        {isMobile ? (
          <MobileSearchBar
            searchClick={openSearchBar}
            searchClick2={openSearchBar2}
            filterCount={filterCount}
          />
        ) : (
          <HeaderWithSearch onSearch={handleNewSearch} />
        )}
      </Box>
      {/* {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: { xs: 0, md: "10rem" },
          }}
        >
          <CircularProgress />
        </Box>
      ) : ( */}
      <Container
        sx={{
          paddingTop: "calc(1rem + var(--banner-height, 0px) + var(--nav-height, 68px) + var(--search-bar-height, 100px))",

          "@media (max-width: 900px)": {
            paddingTop: "calc(var(--banner-height, 0px) + var(--nav-height, 68px))",
            paddingLeft: "0rem",
            paddingRight: "0rem",
          },
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box position="sticky" top="calc(1rem + var(--banner-height, 0px) + var(--nav-height, 68px) + var(--search-bar-height, 100px))">
              {/* MapboxMap Component */}
              <MapboxMap
                yachtLocations={searchData?.yachtLocation || []}
                hoveredLocation={hoveredLocation}
                onClusterClick={handleClusterClick}
                clusterYachts={clusterYachts}
              />
              <Box
                sx={{
                  p: 2,
                  cursor: "pointer",
                  display: { xs: "none", md: "flex" },
                }}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? (
                  <Box display="flex" gap={1} alignItems="center">
                    <RemoveCircleOutlineIcon />
                    <Typography>{t("hide_all_filters")}</Typography>
                  </Box>
                ) : (
                  <Box display="flex" gap={1} alignItems="center">
                    <AddCircleOutlineIcon />
                    <Typography>{t("show_all_filters")}</Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  padding: {
                    md: 2,
                  },
                }}
              >
                {showFilters && (
                  <AllFiltersBox
                    prices={priceHistogramValues}
                    priceMin={priceSliderMin}
                    priceMax={priceSliderMax}
                    yearMin={yearSliderMin}
                    yearMax={yearSliderMax}
                    lengthMin={lengthSliderMinFt}
                    lengthMax={lengthSliderMaxFt}
                    years={years}
                    lengths={lengths}
                    boatsFilter={boatsFilter}
                    setBoatsFilter={setBoatsFilter}
                    cabinsFilter={cabinsFilter}
                    setCabinsFilter={setCabinsFilter}
                    bathroomFilters={bathroomFilters}
                    setBathroomFilter={setBathroomFilter}
                    priceFilter={priceFilter}
                    setPriceFilter={handleUserPriceChange}
                    priceUserSet={priceUserSet}
                    yearFilter={yearFilter}
                    setyearFilter={handleUserYearChange}
                    yearUserSet={yearUserSet}
                    lengthFilter={lengthFilter}
                    setLengthFilter={handleUserLengthChange}
                    lengthUserSet={lengthUserSet}
                    amenitiesFilter={amenitiesFilter}
                    setAmenitiesFilter={setAmenitiesFilter}
                    orderFilter={orderFilter}
                    setOrderFilter={setOrderFilter}
                    buildersInput={buildersInput}
                    setBuildersInput={setBuildersInput}
                    yachtModels={yachtModels}
                    clusterYachts={clusterYachts}
                    setClusterYachts={setClusterYachts}
                  />
                )}
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={8}
            sx={{
              position: "relative",
              top: "-10px",
              borderTopLeftRadius: "50px",
              borderTopRightRadius: "0px",
              bgcolor: "white",
            }}
          >
            {loading || initialLoad ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: { xs: 0, md: "10rem" },
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  mb={2}
                  sx={{
                    px: {
                      xs: "12px",
                      md: "0px",
                    },
                    textAlign: {
                      xs: "center",
                      md: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      marginBottom: "1rem",
                      fontSize: {
                        xs: "18px",
                        md: "24px",
                      },
                    }}
                  >
                    {searchData?.selectedLocation?.city &&
                      searchData?.selectedLocation?.city + ","}{" "}
                    {searchData?.selectedLocation?.countryName &&
                      searchData?.selectedLocation?.countryName + ":"}{" "}
                    {!searchData?.searchResults?.count
                      ? "0"
                      : formatCount(searchData?.searchResults?.count)}{" "}
                    {t("available_text")}
                  </Typography>
                  <FiltersWidget
                    prices={priceHistogramValues}
                    priceMin={priceSliderMin}
                    priceMax={priceSliderMax}
                    yearMin={yearSliderMin}
                    yearMax={yearSliderMax}
                    lengthMin={lengthSliderMinFt}
                    lengthMax={lengthSliderMaxFt}
                    years={years}
                    lengths={lengths}
                    boatsFilter={boatsFilter}
                    setBoatsFilter={setBoatsFilter}
                    cabinsFilter={cabinsFilter}
                    setCabinsFilter={setCabinsFilter}
                    bathroomFilters={bathroomFilters}
                    setBathroomFilter={setBathroomFilter}
                    clusterYachts={clusterYachts}
                    setClusterYachts={setClusterYachts}
                    priceFilter={priceFilter}
                    setPriceFilter={handleUserPriceChange}
                    priceUserSet={priceUserSet}
                    yearFilter={yearFilter}
                    setyearFilter={handleUserYearChange}
                    yearUserSet={yearUserSet}
                    lengthFilter={lengthFilter}
                    setLengthFilter={handleUserLengthChange}
                    lengthUserSet={lengthUserSet}
                    amenitiesFilter={amenitiesFilter}
                    setAmenitiesFilter={setAmenitiesFilter}
                    orderFilter={orderFilter}
                    setOrderFilter={setOrderFilter}
                    buildersInput={buildersInput}
                    setBuildersInput={setBuildersInput}
                    yachtModels={yachtModels}
                    filterCount={filterCount}
                    onClearFilters={clearFilters}
                  />
                </Box>
                <Box>
                  {searchResults?.length > 0 ? (
                    searchResults?.map((yacht: any, index: any) => {
                      const statusInfo = getBoatStatus(yacht?.status);
                      // const filteredImages = [
                      //   ...(yacht?.yacht?.images?.filter(
                      //     (img: any) => img.description === "Main image"
                      //   ) || [
                      //     {
                      //       description: "Main image",
                      //       url: "https://d2h7hm4130kene.cloudfront.net/__1574423637__/images/zone/9_small_2x_q90.webp",
                      //     },
                      //   ]),
                      //   ...(yacht?.yacht?.images
                      //     ?.filter(
                      //       (img: any) => img.description !== "Main image"
                      //     )
                      //     .slice(0, 4) || []),
                      // ].slice(0, 5);
                      const allImages = [
                        ...(yacht?.yacht?.images?.filter(
                          (img: any) => img.description === "Main image",
                        ) || []),
                        ...(yacht?.yacht?.images?.filter(
                          (img: any) => img.description !== "Main image",
                        ) || []),
                      ].slice(0, 5);
                      const filteredImages = allImages.length > 0
                        ? allImages
                        : [{ description: "Main image", url: boatPlaceholder }];
                      return (
                          <Card
                            key={index}
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", lg: "row" },
                              justifyContent: "space-between",
                              mb: "1.5rem",
                              borderRadius: "16px",
                              overflow: "hidden",
                              mx: { xs: "10px", lg: 0 },
                            }}
                          >
                            <Box
                              sx={{
                                width: { xs: "100%", lg: "280px", xl: "280px" },
                                height: { xs: "200px", lg: "250px" },
                                flexShrink: 0,
                                cursor: "pointer",
                                position: "relative",
                                overflow: "hidden",
                                "& .slick-slider, & .slick-list, & .slick-track": {
                                  height: "100%",
                                },
                                "& .slick-slide > div": {
                                  height: "100%",
                                },
                              }}
                              onClick={() => handleYachtClick(yacht)}
                              onMouseEnter={() => setIsHovered(index)}
                              onMouseLeave={() => setIsHovered(null)}
                            >
                              <Carousel
                                ref={(ref) =>
                                  (carouselRefs.current[index] = ref)
                                }
                                arrows={false}
                                infinite={true}
                                dots={false}
                                speed={300}
                                afterChange={(current) =>
                                  setCurrentSlides((prev: any) => ({
                                    ...prev,
                                    [index]: current,
                                  }))
                                }
                                style={{ height: "100%" }}
                              >
                                {filteredImages.map(
                                  (image: any, imgIndex: number) => (
                                    <Box
                                      key={imgIndex}
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      <CardMedia
                                        component="img"
                                        width="100%"
                                        height="100%"
                                        image={imageUrl(image.url)}
                                        alt={
                                          yacht?.yacht?.name || "Yacht Image"
                                        }
                                        loading="lazy"
                                        sx={{
                                          objectFit: "cover",
                                        }}
                                      />
                                    </Box>
                                  ),
                                )}
                              </Carousel>

                              {filteredImages?.length > 1 && (
                                <>
                                  <CarouselArrows
                                    onPrevClick={(e: any) => {
                                      e.stopPropagation();
                                      carouselRefs.current[index]?.prev();
                                    }}
                                    onNextClick={(e: any) => {
                                      e.stopPropagation();
                                      carouselRefs.current[index]?.next();
                                    }}
                                    show={
                                      filteredImages?.length > 1 &&
                                      isHovered === index
                                    }
                                  />
                                  {/* {isHovered === index && (
                                    <>
                                      <Box
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          carouselRefs.current[index]?.prev();
                                        }}
                                        sx={{
                                          position: "absolute",
                                          top: "50%",
                                          left: "12px",
                                          transform: "translateY(-50%)",
                                          zIndex: 2,
                                          width: "32px",
                                          height: "32px",
                                          backgroundColor:
                                            "rgba(255, 255, 255, 0.9)",
                                          borderRadius: "50%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                          opacity: 1,
                                          transition: "all 0.3s ease",
                                          boxShadow:
                                            "0 2px 8px rgba(0, 0, 0, 0.15)",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(255, 255, 255, 1)",
                                            transform:
                                              "translateY(-50%) scale(1.1)",
                                          },
                                        }}
                                      >
                                        <ChevronLeft
                                          sx={{
                                            fontSize: "18px",
                                            color: "#333",
                                          }}
                                        />
                                      </Box>

                                      <Box
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          carouselRefs.current[index]?.next();
                                        }}
                                        sx={{
                                          position: "absolute",
                                          top: "50%",
                                          right: "12px",
                                          transform: "translateY(-50%)",
                                          zIndex: 2,
                                          width: "32px",
                                          height: "32px",
                                          backgroundColor:
                                            "rgba(255, 255, 255, 0.9)",
                                          borderRadius: "50%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                          opacity: 1,
                                          transition: "all 0.3s ease",
                                          boxShadow:
                                            "0 2px 8px rgba(0, 0, 0, 0.15)",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(255, 255, 255, 1)",
                                            transform:
                                              "translateY(-50%) scale(1.1)",
                                          },
                                        }}
                                      >
                                        <ChevronRight
                                          style={{
                                            fontSize: "14px",
                                            color: "#333",
                                          }}
                                        />
                                      </Box>
                                    </>
                                  )} */}

                                  {/* Custom Dots */}
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: "12px",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      display: "flex",
                                      gap: "6px",
                                      zIndex: 2,
                                      opacity: 1,
                                      transition: "opacity 0.3s ease",
                                    }}
                                  >
                                    {(() => {
                                      const currentIndex =
                                        currentSlides[index] || 0;
                                      const { start, end } =
                                        getVisibleDotsRange(
                                          filteredImages.length,
                                          currentIndex,
                                          5,
                                        );
                                      const visibleDots = [];

                                      for (let i = start; i <= end; i++) {
                                        visibleDots.push(
                                          <Box
                                            key={i}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              carouselRefs.current[index]?.goTo(
                                                i,
                                              );
                                            }}
                                            sx={{
                                              width: "8px",
                                              height: "8px",
                                              borderRadius: "50%",
                                              backgroundColor:
                                                currentIndex === i
                                                  ? "#fff"
                                                  : "rgba(255, 255, 255, 0.6)",
                                              cursor: "pointer",
                                              transition: "all 0.2s ease",
                                              "&:hover": {
                                                backgroundColor: "#fff",
                                                transform: "scale(1.2)",
                                              },
                                            }}
                                          />,
                                        );
                                      }

                                      return visibleDots;
                                    })()}
                                  </Box>
                                  {/* <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: "12px",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      display: "flex",
                                      gap: "6px",
                                      zIndex: 2,
                                      opacity: 1,
                                      transition: "opacity 0.3s ease",
                                    }}
                                  >
                                    {filteredImages?.map(
                                      (_: any, imgIndex: any) => (
                                        <Box
                                          key={imgIndex}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            carouselRefs.current[index]?.goTo(
                                              imgIndex
                                            );
                                          }}
                                          sx={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor:
                                              (currentSlides[index] || 0) ===
                                              imgIndex
                                                ? "#fff"
                                                : "rgba(255, 255, 255, 0.6)",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              backgroundColor: "#fff",
                                              transform: "scale(1.2)",
                                            },
                                          }}
                                        />
                                      )
                                    )}
                                  </Box> */}
                                </>
                              )}
                            </Box>

                            <CardContent
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                flex: 1,
                              }}
                              onMouseEnter={() => {
                                handleYachtHover(
                                  yacht?.location?.latitude,
                                  yacht?.location?.longitude,
                                );
                                setIsHovered(index);
                              }}
                              onMouseLeave={() => {
                                setHoveredLocation(null);
                                setIsHovered(null);
                              }}
                            >
                              <Box
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleYachtClick(yacht)}
                                onMouseEnter={() => {
                                  handleYachtHover(
                                    yacht?.location?.latitude,
                                    yacht?.location?.longitude,
                                  );
                                  setIsHovered(index);
                                }}
                                onMouseLeave={() => {
                                  setHoveredLocation(null);
                                  setIsHovered(null);
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {yacht?.yacht?.model || "Yacht Name"}{" "}
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "inline-block",
                                        position: "relative",
                                      }}
                                    >
                                      <Tooltip
                                        title={
                                          <Box sx={{ padding: "5px" }}>
                                            <Typography
                                              sx={{
                                                fontWeight: 700,
                                                fontSize: "16px",
                                                mb: 0.5,
                                              }}
                                            >
                                              {statusInfo.title}
                                            </Typography>
                                            <Typography
                                              sx={{ fontSize: "14px" }}
                                            >
                                              {statusInfo.description}
                                            </Typography>
                                          </Box>
                                        }
                                        arrow
                                        placement="top"
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: "#fff",
                                              color: "#393c3d",
                                              border: "1px solid #e0e0e0",
                                              borderRadius: "12px",
                                              boxShadow:
                                                "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                              width: "220px",
                                              maxWidth: "none",
                                              whiteSpace: "normal",
                                            },
                                          },
                                          arrow: {
                                            sx: {
                                              color: "#fff",
                                              fontSize: 20,
                                              "&::before": {
                                                filter:
                                                  "drop-shadow(0px 0px 1px rgba(0,0,0,0.50))",
                                              },
                                            },
                                          },
                                        }}
                                      >
                                        {statusInfo.title ===
                                        "Confirmed Availability" ? (
                                          <DoneIcon
                                            sx={{
                                              color: "green",
                                              height: "25px",
                                              cursor: "pointer",
                                            }}
                                          />
                                        ) : (
                                          <CircleIcon
                                            sx={{
                                              color: statusInfo.color,
                                              height: "14px",
                                              cursor: "pointer",
                                            }}
                                          />
                                        )}
                                      </Tooltip>
                                    </Box>
                                  </Typography>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "0.5rem", flexWrap: "wrap" }}>
                                  <Typography variant="body2" sx={{ color: "#666" }}>
                                    {yacht.startBase} → {yacht.endBase}
                                  </Typography>
                                  {yacht.startBase && yacht.endBase && yacht.startBase !== yacht.endBase && (
                                    <Chip
                                      label="One Way"
                                      size="small"
                                      sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600, fontSize: "11px", height: "20px", border: "1px solid #90caf9" }}
                                    />
                                  )}
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#666",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <img
                                    src="https://d2h7hm4130kene.cloudfront.net/__1607705537__/images/smallboat.svg"
                                    alt="Small Boat"
                                    style={{
                                      width: "14px",
                                      height: "14px",
                                      marginRight: "8px", // Adds spacing between the icon and text
                                      position: "relative",
                                      top: "2px",
                                    }}
                                  />
                                  year {yacht?.yacht?.year} ·{" "}
                                  {yacht?.yacht?.cabins} cabins · length{" "}
                                  {yacht?.yacht?.length ? `${Math.round(yacht.yacht.length * M_TO_FT)} ft` : ""} ·{" "}
                                  {yacht?.yacht?.wc === 1
                                    ? "1 bathroom"
                                    : `${yacht?.yacht?.wc || 0} bathrooms`}{" "}
                                  · {yacht?.yacht?.kind}
                                </Typography>
                                {/* Equipment chips */}
                                {yacht?.yacht?.equipment_json?.length > 0 && (() => {
                                  const validItems = yacht.yacht.equipment_json
                                    .filter((e: any) => {
                                      const name = e.name?.trim() || "";
                                      const val = String(e.value ?? "").toLowerCase();
                                      return name && !name.toLowerCase().includes("unknown") && (val === "true" || val === "");
                                    });
                                  const items = validItems.slice(0, 8);
                                  const overflow = validItems.length - 8;
                                  return (
                                    <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                                      {items.map((e: any, i: number) => (
                                        <Chip
                                          key={e.id ?? i}
                                          label={e.name}
                                          size="small"
                                          variant="outlined"
                                          icon={getEquipmentIcon(e.name) ?? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                                          sx={{ fontSize: "11px", height: "24px", color: "#555", borderColor: "#ddd" }}
                                        />
                                      ))}
                                      {overflow > 0 && (
                                        <Chip
                                          label={`+${overflow}`}
                                          size="small"
                                          sx={{ fontSize: "11px", height: "24px", color: "#888", bgcolor: "#f5f5f5" }}
                                        />
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>
                              <Box pt="10px" pb="6px">
                                {/* Discount badge — only when there is a discount */}
                                {yacht.discountPercentage > 0 && (
                                  <Box
                                    sx={{
                                      display: "inline-block",
                                      padding: "0.25rem 0.75rem",
                                      border: "1px solid #ccc",
                                      borderRadius: "16px",
                                      color: "#666",
                                      fontWeight: "bold",
                                      fontSize: "14px",
                                      backgroundColor: "#ffffff",
                                      mb: 1,
                                    }}
                                  >
                                    {yacht.discountPercentage > 15
                                      ? `🔥 -${yacht.discountPercentage}% Super Discount!`
                                      : `-${yacht.discountPercentage}% Discount`}
                                  </Box>
                                )}

                                {/* Price block — always full-width, right-aligned */}
                                <Box sx={{ textAlign: "right" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    {yacht.discountPercentage > 0 && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#666",
                                          textDecoration: "line-through",
                                        }}
                                      >
                                        {formatPrice(yacht.actualStartPrice)}
                                      </Typography>
                                    )}
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "bold",
                                        color: "#000",
                                        fontSize: { xs: "14px", md: "18px" },
                                      }}
                                    >
                                      {formatPrice(yacht.actualPrice)}
                                    </Typography>
                                  </Box>
                                  {yacht.obligatoryExtrasPrice > 0 && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#888", display: "block", mt: 0.25 }}
                                    >
                                      + {formatPrice(Math.round(yacht.obligatoryExtrasPrice))} mandatory extras
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {/* Compare toggle — isolated component so only the button re-renders, not all cards */}
                              {(() => {
                                const yid = String(yacht.yachtId ?? yacht.id ?? '');
                                const mainImg = (yacht?.yacht?.images || []).find((im: any) => im.description === "Main image");
                                const urlP = new URLSearchParams(location.search);
                                return (
                                  <CompareToggleButton
                                    yachtId={yid}
                                    model={yacht?.yacht?.model || 'Unknown'}
                                    imageUrl={mainImg?.url || ''}
                                    price={yacht.actualPrice || 0}
                                    dateFrom={urlP.get('date_from') || yacht.dateFrom || ''}
                                    dateTo={urlP.get('date_to') || yacht.dateTo || ''}
                                    status={String(yacht?.status ?? '')}
                                  />
                                );
                              })()}
                            </CardContent>
                          </Card>
                      );
                    })
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        width: "100%",
                        py: 4,
                        color: "#666",
                        fontWeight: 500,
                        fontSize: "1rem",
                      }}
                    >
                      No boats match your search criteria
                    </Box>
                  )}

                  {/* Pagination Controls */}
                  {searchResults?.length > 0 && totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        my: "2rem",
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, newPage) =>
                          handleSearchNext({
                            api: isFilterApplied
                              ? newPage > page
                                ? searchData.filterNext
                                : searchData.filterPrev
                              : newPage > page
                                ? searchData.searchResults.next
                                : searchData.searchResults.previous,
                            page: newPage,
                          })
                        }
                        color="primary"
                        shape="rounded"
                        siblingCount={1}
                        boundaryCount={1}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </Container>
      {/* // )} */}
      <Dialog
        open={isOpenModal}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            width: "100%",
            height: "auto",
            m: 0,
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          },
        }}
        sx={{
          alignItems: "flex-start",
          height: "auto",
          // 👈 forces dialog to align top
        }}
      >
        <DialogTitle sx={{ px: 2, py: 1 }}>
          <Grid container alignItems="center">
            {/* Column 1: Empty */}
            <Grid item xs={4}></Grid>

            {/* Column 2: Centered Text */}
            <Grid item xs={4}>
              <Typography variant="h6" fontWeight="bold" align="center">
                Where are we going?
              </Typography>
            </Grid>

            {/* Column 3: Close Icon on Right */}
            <Grid item xs={4} display="flex" justifyContent="flex-end">
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider sx={{ width: "100%", my: 2 }} />

        <DialogContent sx={{ px: 2 }}>
          <HeaderWithSearch onSearch={handleNewSearch} />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isOpenModal2}
        onClose={onClose2}
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%", // Full height
            m: 0,
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "100vh", // Ensure it doesn't exceed viewport
          },
        }}
      >
        {/* Fixed Header */}
        <Box sx={{ flexShrink: 0 }}>
          <DialogTitle sx={{ px: 2, py: 1 }}>
            <Grid container alignItems="center">
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <Typography variant="h6" fontWeight="bold" align="center">
                  Filters
                </Typography>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="flex-end">
                <IconButton onClick={onClose2}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </DialogTitle>
          <Divider />
        </Box>

        {/* Scrollable Content */}
        <DialogContent
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box p={2}>
            <AllFiltersBox
              prices={priceHistogramValues}
              priceMin={priceSliderMin}
              priceMax={priceSliderMax}
              yearMin={yearSliderMin}
              yearMax={yearSliderMax}
              lengthMin={lengthSliderMinFt}
              lengthMax={lengthSliderMaxFt}
              years={years}
              lengths={lengths}
              boatsFilter={boatsFilter}
              setBoatsFilter={setBoatsFilter}
              cabinsFilter={cabinsFilter}
              setCabinsFilter={setCabinsFilter}
              bathroomFilters={bathroomFilters}
              setBathroomFilter={setBathroomFilter}
              priceFilter={priceFilter}
              setPriceFilter={handleUserPriceChange}
              priceUserSet={priceUserSet}
              yearFilter={yearFilter}
              setyearFilter={handleUserYearChange}
              yearUserSet={yearUserSet}
              lengthFilter={lengthFilter}
              setLengthFilter={handleUserLengthChange}
              lengthUserSet={lengthUserSet}
              amenitiesFilter={amenitiesFilter}
              setAmenitiesFilter={setAmenitiesFilter}
              orderFilter={orderFilter}
              setOrderFilter={setOrderFilter}
              buildersInput={buildersInput}
              setBuildersInput={setBuildersInput}
              yachtModels={yachtModels}
              clusterYachts={clusterYachts}
              setClusterYachts={setClusterYachts}
            />
          </Box>
        </DialogContent>

        {/* Fixed Footer */}
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #ddd",
            backgroundColor: "white",
          }}
        >
          <Typography variant="body1" fontWeight="bold" onClick={clearFilters}>
            Remove Filters
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={fetchFilteredYachts}
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(to right, #000, #333)",
            }}
          >
            Search
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SearchResults;
